import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  standalone: false
})
export class TimePickerComponent {
  private static nextPickerId = 0;

  @Input() pickerId = `time-picker-${TimePickerComponent.nextPickerId++}`;
  @Input() label = '';
  @Input() value: string | null = null;
  @Input() displayFormat: '12-hour' | '24-hour' = '12-hour';
  @Input() showNowButton = false;
  @Input() showError = false;
  @Input() errorMessage = '';
  @Output() readonly timeChange = new EventEmitter<string>();
  @Output() readonly nowClicked = new EventEmitter<void>();

  readonly hours = Array.from({ length: 24 }, (_, hour) => this.pad(hour));
  minuteOptions: readonly string[] = this.createMinuteOptions(1);
  draftHours = '00';
  draftMinutes = '00';
  isOpen = false;

  private currentMinuteStep = 1;

  @Input()
  set minuteStep(value: number) {
    if (!Number.isInteger(value) || value < 1 || 60 % value !== 0) {
      throw new RangeError('minuteStep must be a positive integer that divides 60.');
    }

    this.currentMinuteStep = value;
    this.minuteOptions = this.createMinuteOptions(value);
  }

  get minuteStep(): number {
    return this.currentMinuteStep;
  }

  get displayValue(): string {
    if (!this.value) {
      return '';
    }

    const [hourPart = '', minutePart = ''] = this.value.split(':');
    const hour = Number(hourPart);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !/^\d{2}$/.test(minutePart)) {
      return this.value;
    }

    if (this.displayFormat === '24-hour') {
      return `${this.pad(hour)}:${minutePart}`;
    }

    return `${this.pad(hour % 12 || 12)}:${minutePart} ${hour < 12 ? 'AM' : 'PM'}`;
  }

  openPicker(): void {
    const [hours = '00', minutes = '00'] = this.value?.split(':') ?? [];
    this.draftHours = this.pad(Number(hours));
    this.draftMinutes = this.pad(Number(minutes));
    this.isOpen = true;
  }

  clearPicker(): void {
    this.draftHours = '00';
    this.draftMinutes = '00';
  }

  confirmPicker(): void {
    this.timeChange.emit(`${this.draftHours}:${this.draftMinutes}`);
    this.isOpen = false;
  }

  closePicker(): void {
    this.isOpen = false;
  }

  private createMinuteOptions(step: number): readonly string[] {
    return Array.from({ length: 60 / step }, (_, index) => this.pad(index * step));
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
}
